import React, { useState, useEffect, useCallback } from 'react';
import { Play, Square, Loader2, MapPin, RefreshCw, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import canvassingService from '../../services/canvassingService';
import userService from '../../services/userService';
import useScrapeStream from '../../hooks/useScrapeStream';
import useConfirm from '../../hooks/useConfirm';
import ConfirmDialog from '../ConfirmDialog';
import { useToast } from '../Toast';

// Strip postcode suffix from territory name, e.g. "Liam Matthews (LE1)" → "liam matthews"
function getTerritoryBaseName(memberName) {
  return (memberName || '').replace(/\s*\([A-Z]+\d*\)\s*$/, '').trim().toLowerCase();
}

const ScrapeTab = () => {
  const [territories, setTerritories] = useState([]);
  const [techGroups, setTechGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [scrapeJobs, setScrapeJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTown, setSelectedTown] = useState('');
  const [selectedModel, setSelectedModel] = useState('openai/gpt-4o-mini');
  const [newOnly, setNewOnly] = useState(false);
  const [coverageStats, setCoverageStats] = useState(null);
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [error, setError] = useState(null);

  const toast = useToast();
  const { confirmDialog, confirm } = useConfirm();

  const {
    isConnected,
    progress,
    validationProgress,
    isCompleted,
    hasError,
    completedStats,
    connectToScrapeStream,
    resetScrapeStream
  } = useScrapeStream();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [terr, cats, jobs, techResp, franchiseeResp] = await Promise.all([
        canvassingService.getTerritories(),
        canvassingService.getCategories(),
        canvassingService.getScrapeJobs(),
        userService.getUsers({ role: 'technician', limit: 100, active: true }),
        userService.getUsers({ role: 'franchisee', limit: 100, active: true })
      ]);
      const allTerritories = terr.territories || [];
      setTerritories(allTerritories);
      setCategories(cats.categories || []);
      setScrapeJobs(jobs.jobs || []);

      const franchisees = franchiseeResp.users || [];
      const techs = techResp.users || [];

      // Build name→territory lookup (strip postcode codes from territory names)
      const nameToTerritory = {};
      for (const t of allTerritories) {
        nameToTerritory[getTerritoryBaseName(t.member_name)] = t;
      }

      // Group by franchise, matching Calendar layout exactly
      const groups = franchisees.map(f => {
        // Find technicians assigned to this franchisee
        const franchiseTechs = techs
          .filter(t => t.assigned_franchisee_id === f.id)
          .map(t => {
            const territory = nameToTerritory[t.name.toLowerCase()];
            return { id: t.id, name: t.name, member_id: territory?.member_id || null, towns: territory?.towns || [] };
          });

        // Include the franchisee themselves (they may also have a territory)
        const franchiseeTerritory = nameToTerritory[f.name.toLowerCase()];
        const franchiseeEntry = {
          id: f.id, name: f.name, member_id: franchiseeTerritory?.member_id || null,
          towns: franchiseeTerritory?.towns || [], isFranchisee: true
        };

        return {
          id: f.id,
          name: f.name,
          company: f.company_name || '',
          franchisee: franchiseeEntry,
          technicians: franchiseTechs
        };
      });

      setTechGroups(groups);
    } catch (err) {
      setError('Failed to load data');
      console.error('loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // On mount, check for a running job and reconnect SSE
  useEffect(() => {
    const reconnectIfRunning = async () => {
      try {
        const data = await canvassingService.getScrapeJobs(5);
        const jobs = data.jobs || [];
        const runningJob = jobs.find(j => j.status === 'running');
        if (runningJob) {
          setActiveJobId(runningJob.id);
          connectToScrapeStream(runningJob.id);
        }
      } catch (err) {
        // Silently fail — not critical
      }
    };
    reconnectIfRunning();
  }, [connectToScrapeStream]);

  // Poll active job status as fallback in case SSE misses the completed event
  useEffect(() => {
    if (!activeJobId || isCompleted || hasError) return;

    const poll = setInterval(async () => {
      try {
        const data = await canvassingService.getScrapeJob(activeJobId);
        const job = data.job;
        if (job && (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled')) {
          clearInterval(poll);
          loadData();
          if (job.status === 'completed') {
            toast.success(`Extraction complete — ${job.leads_found || 0} leads found (${job.new_leads || 0} new, ${job.chains_found || 0} chains)`);
          } else {
            toast.error(`Extraction ${job.status}${job.error_message ? ` — ${job.error_message}` : ''}`);
          }
          setActiveJobId(null);
        }
      } catch {}
    }, 5000);

    return () => clearInterval(poll);
  }, [activeJobId, isCompleted, hasError, loadData]);

  // Refresh jobs list and show toast when scrape completes or errors
  useEffect(() => {
    if (isCompleted) {
      loadData();
      const stats = completedStats || {};
      const leads = stats.totalLeads || stats.leadsFound || 0;
      const newLeads = stats.newLeads || 0;
      const chains = stats.chains || stats.chainsFound || 0;
      toast.success(`Extraction complete — ${leads} leads found (${newLeads} new, ${chains} chains)`);
    }
  }, [isCompleted, completedStats, loadData]);

  useEffect(() => {
    if (hasError) {
      loadData();
      toast.error('Extraction failed — check logs for details');
    }
  }, [hasError, loadData]);

  // Fetch coverage stats when member/category changes
  useEffect(() => {
    const fetchCoverage = async () => {
      try {
        setLoadingCoverage(true);
        const params = {};
        if (selectedMember) {
          if (selectedMember.startsWith('f:')) {
            // Franchise-level — collect all member IDs for this franchise
            const group = techGroups.find(g => g.id === selectedMember.slice(2));
            if (group) {
              const ids = [group.franchisee, ...group.technicians]
                .filter(m => m.member_id)
                .map(m => m.member_id);
              if (ids.length > 0) params.memberIds = ids.join(',');
            }
          } else {
            // Individual user — find their territory member_id
            const allMembers = techGroups.flatMap(g => [g.franchisee, ...g.technicians]);
            const member = allMembers.find(m => m.id === selectedMember);
            if (member?.member_id) params.memberId = member.member_id;
          }
        }
        if (selectedCategory) params.categoryId = selectedCategory;
        const stats = await canvassingService.getCoverageStats(params);
        setCoverageStats(stats);
      } catch (err) {
        console.error('Failed to load coverage stats:', err);
        setCoverageStats(null);
      } finally {
        setLoadingCoverage(false);
      }
    };
    fetchCoverage();
  }, [selectedMember, selectedCategory, techGroups]);

  const handleStartScrape = async () => {
    // Build confirm message
    const memberLabel = selectedMemberInfo ? selectedMemberInfo.label : 'All Members';
    const categoryLabel = selectedCategory
      ? categories.find(c => c.id === parseInt(selectedCategory))?.category_name || 'Selected'
      : 'All Categories';
    const townLabel = selectedTown || 'All Towns';
    const modeLabel = newOnly ? '\nMode: New combinations only' : '';
    const confirmed = await confirm({
      title: 'Start Extraction',
      message: `Member: ${memberLabel}\nTown: ${townLabel}\nCategory: ${categoryLabel}${modeLabel}`,
      variant: 'info',
      confirmLabel: 'Start'
    });
    if (!confirmed) return;

    try {
      setIsStarting(true);
      setError(null);
      resetScrapeStream();

      // Determine memberId vs memberIds based on selection type
      const scrapeParams = {
        categoryId: selectedCategory ? parseInt(selectedCategory) : null,
        town: selectedTown || null,
        aiModel: selectedModel || null,
        newOnly
      };
      if (selectedMember) {
        if (selectedMember.startsWith('f:')) {
          // Franchise-level — send all member IDs
          const group = techGroups.find(g => g.id === selectedMember.slice(2));
          if (group) {
            const ids = [group.franchisee, ...group.technicians]
              .filter(m => m.member_id)
              .map(m => m.member_id);
            if (ids.length > 0) scrapeParams.memberIds = ids;
          }
        } else {
          // Individual user — find their territory member_id
          const allMembers = techGroups.flatMap(g => [g.franchisee, ...g.technicians]);
          const member = allMembers.find(m => m.id === selectedMember);
          if (member?.member_id) scrapeParams.memberId = member.member_id;
        }
      }

      const result = await canvassingService.startScrape(scrapeParams);

      if (result.jobId) {
        setActiveJobId(result.jobId);
        // Connect SSE FIRST so we don't miss early progress events
        await connectToScrapeStream(result.jobId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start scrape');
    } finally {
      setIsStarting(false);
    }
  };

  const handleCancelScrape = async () => {
    if (!activeJobId) return;
    try {
      await canvassingService.cancelScrape(activeJobId);
    } catch (err) {
      setError('Failed to cancel scrape');
    }
  };

  // Resolve selected member info (works for both individual and franchise-level)
  const selectedMemberInfo = (() => {
    if (!selectedMember) return null;
    if (selectedMember.startsWith('f:')) {
      const group = techGroups.find(g => g.id === selectedMember.slice(2));
      if (!group) return null;
      const allMembers = [group.franchisee, ...group.technicians];
      const allTowns = allMembers.flatMap(m => m.towns || []);
      const seen = new Set();
      const uniqueTowns = allTowns.filter(t => {
        if (seen.has(t.town)) return false;
        seen.add(t.town);
        return true;
      }).sort((a, b) => a.town.localeCompare(b.town));
      return {
        label: `${group.name} — all`,
        member_name: group.name,
        towns: uniqueTowns
      };
    }
    // Individual user
    const allMembers = techGroups.flatMap(g => [g.franchisee, ...g.technicians]);
    const member = allMembers.find(m => m.id === selectedMember);
    if (!member) return null;
    return {
      label: member.name,
      member_name: member.name,
      towns: member.towns || []
    };
  })();
  const isRunning = progress && !isCompleted && !hasError;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-pest-blue" size={24} />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><XCircle size={16} /></button>
        </div>
      )}

      {/* Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-800 mb-4">Extraction Settings</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Member selector — grouped by franchise */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Member</label>
            <select
              value={selectedMember}
              onChange={(e) => { setSelectedMember(e.target.value); setSelectedTown(''); }}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              disabled={isRunning}
            >
              <option value="">All Members</option>
              {techGroups.map(group => (
                <optgroup key={group.id} label={group.company || group.name}>
                  <option value={`f:${group.id}`}>
                    {group.name} ({group.franchisee.towns?.length || 0} towns)
                  </option>
                  {group.technicians.map(tech => (
                    <option key={tech.id} value={tech.id}>
                      {tech.name} ({tech.towns?.length || 0} towns)
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Category selector */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              disabled={isRunning}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.category_name} ({c.phrases?.length || 0} phrases)
                </option>
              ))}
            </select>
          </div>

          {/* Town selector */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Town</label>
            <select
              value={selectedTown}
              onChange={(e) => setSelectedTown(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              disabled={isRunning}
            >
              <option value="">All Towns{selectedMemberInfo ? ` (${selectedMemberInfo.towns.length})` : ''}</option>
              {selectedMemberInfo && selectedMemberInfo.towns
                .sort((a, b) => a.town.localeCompare(b.town))
                .map((t, i) => (
                  <option key={i} value={t.town}>{t.town}</option>
                ))
              }
            </select>
          </div>

          {/* Validation selector */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Validation</label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              disabled={isRunning}
            >
              <option value="">No Validation</option>
              <option value="openai/gpt-4o-mini">Standard</option>
              <option value="google/gemini-2.0-flash-001">Fast</option>
            </select>
          </div>
        </div>

        {/* New Only checkbox + coverage stats */}
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={newOnly}
              onChange={(e) => setNewOnly(e.target.checked)}
              disabled={isRunning}
              className="rounded border-gray-300 text-pest-blue focus:ring-pest-blue"
            />
            <span className="text-sm text-gray-700">Scrape new combinations only</span>
          </label>

          {coverageStats && !loadingCoverage && (
            <span className="text-xs text-gray-500">
              {coverageStats.coveredCombos} of {coverageStats.totalCombos} combinations already covered
              {coverageStats.coveragePercent > 0 && (
                <span className="ml-1 text-pest-blue font-medium">({coverageStats.coveragePercent}%)</span>
              )}
              {newOnly && coverageStats.uncoveredCombos > 0 && (
                <span className="ml-1 text-green-600 font-medium">
                  — {coverageStats.uncoveredCombos} to scrape
                </span>
              )}
            </span>
          )}
          {loadingCoverage && (
            <span className="text-xs text-gray-400">Loading coverage...</span>
          )}
        </div>

        {/* Selected member towns preview */}
        {selectedMemberInfo && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <MapPin size={14} className="text-gray-500" />
              <span className="text-xs font-medium text-gray-600">
                {selectedMemberInfo.member_name}'s {selectedMemberInfo.towns.length} towns:
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {selectedMemberInfo.towns.map((t, i) => (
                <span key={i} className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-600">
                  {t.town}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Start/Cancel button */}
        <div className="flex items-center space-x-3">
          {isRunning ? (
            <button
              onClick={handleCancelScrape}
              className="flex items-center space-x-2 px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium"
            >
              <Square size={16} /><span>Cancel Extraction</span>
            </button>
          ) : (
            <button
              onClick={handleStartScrape}
              disabled={isStarting}
              className="flex items-center space-x-2 px-6 py-2.5 bg-pest-blue text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {isStarting ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
              <span>{isStarting ? 'Starting...' : 'Start Extraction'}</span>
            </button>
          )}

          {isRunning && isConnected && (
            <span className="flex items-center space-x-2 text-xs text-gray-600">
              <Loader2 size={14} className={`animate-spin ${
                progress?.phase === 'validating' ? 'text-purple-600' : 'text-blue-600'
              }`} />
              <span className={progress?.phase === 'validating' ? 'text-purple-600 font-medium' : 'text-blue-600 font-medium'}>
                {progress?.phase === 'validating'
                  ? `Validating${validationProgress ? ` ${validationProgress.validated}/${validationProgress.total}` : '...'}`
                  : progress?.currentTown
                    ? `Scraping ${progress.currentTown}`
                    : 'Extracting...'
                }
              </span>
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            </span>
          )}
        </div>
      </div>

      {/* Live Progress — only show when we have real extraction progress */}
      {progress && progress.totalPhrases > 0 && (() => {
        // Determine current phase intelligently
        const scrapingDone = (progress.completedPhrases || 0) >= progress.totalPhrases;
        const inValidation = progress.phase === 'validating' || (scrapingDone && validationProgress);
        const scrapePercent = progress && progress.totalPhrases > 0
          ? Math.round(progress.completedPhrases / progress.totalPhrases * 100) : 0;
        const valPercent = validationProgress && validationProgress.total > 0
          ? Math.round(validationProgress.validated / validationProgress.total * 100) : 0;

        return (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-3">
              {isCompleted ? 'Extraction Complete' : hasError ? 'Extraction Failed' : 'Live Progress'}
            </h3>

            {/* Phase 1: Data Extraction */}
            <div className={`rounded-lg border p-3 mb-3 ${
              isCompleted ? 'border-green-200 bg-green-50' :
              scrapingDone || inValidation ? 'border-green-200 bg-green-50' :
              'border-blue-200 bg-blue-50'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {scrapingDone || inValidation || isCompleted ? (
                    <CheckCircle size={16} className="text-green-600" />
                  ) : (
                    <Loader2 size={16} className="text-blue-600 animate-spin" />
                  )}
                  <span className={`text-sm font-medium ${
                    scrapingDone || inValidation || isCompleted ? 'text-green-700' : 'text-blue-700'
                  }`}>
                    Phase 1: Data Extraction
                  </span>
                </div>
                <span className={`text-xs font-medium ${
                  scrapingDone || inValidation || isCompleted ? 'text-green-600' : 'text-blue-600'
                }`}>
                  {scrapingDone || inValidation || isCompleted ? 'Complete' : `${scrapePercent}%`}
                </span>
              </div>

              {/* Scraping progress bar */}
              <div className="w-full bg-white/60 rounded-full h-1.5 mb-2">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    scrapingDone || inValidation || isCompleted ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${scrapingDone || inValidation || isCompleted ? 100 : scrapePercent}%` }}
                />
              </div>

              {/* Details row */}
              <div className="flex items-center justify-between text-xs">
                <span className={scrapingDone || inValidation || isCompleted ? 'text-green-600' : 'text-blue-600'}>
                  {!scrapingDone && !inValidation && !isCompleted && progress?.currentTown ? (
                    <>Scraping <strong>{progress.currentTown}</strong> — "{progress.currentPhrase}"</>
                  ) : (
                    <>{progress?.completedPhrases || 0}/{progress?.totalPhrases || 0} combinations · {progress?.completedTowns || 0}/{progress?.totalTowns || 0} towns</>
                  )}
                </span>
                <span className="text-gray-500">
                  {progress?.leadsFound || 0} leads ({progress?.newLeads || 0} new) · {progress?.apiCalls || 0} API calls
                </span>
              </div>
              {progress?.skippedCombos > 0 && (
                <div className="mt-1 text-xs text-gray-500">
                  {progress.skippedCombos} combinations skipped (already covered)
                </div>
              )}
            </div>

            {/* Phase 2: AI Validation (only show when scraping is done or has validation progress) */}
            {(inValidation || isCompleted) && (
              <div className={`rounded-lg border p-3 ${
                isCompleted ? 'border-green-200 bg-green-50' :
                'border-purple-200 bg-purple-50'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {isCompleted ? (
                      <CheckCircle size={16} className="text-green-600" />
                    ) : (
                      <Loader2 size={16} className="text-purple-600 animate-spin" />
                    )}
                    <span className={`text-sm font-medium ${isCompleted ? 'text-green-700' : 'text-purple-700'}`}>
                      Phase 2: AI Validation
                    </span>
                  </div>
                  <span className={`text-xs font-medium ${isCompleted ? 'text-green-600' : 'text-purple-600'}`}>
                    {isCompleted ? 'Complete' : validationProgress ? `${valPercent}%` : 'Starting...'}
                  </span>
                </div>

                {/* Validation progress bar */}
                <div className="w-full bg-white/60 rounded-full h-1.5 mb-2">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${isCompleted ? 'bg-green-500' : 'bg-purple-500'}`}
                    style={{ width: `${isCompleted ? 100 : valPercent}%` }}
                  />
                </div>

                {/* Details row */}
                <div className="flex items-center justify-between text-xs">
                  <span className={isCompleted ? 'text-green-600' : 'text-purple-600'}>
                    {validationProgress
                      ? <><strong>{validationProgress.validated}</strong> of <strong>{validationProgress.total}</strong> leads validated</>
                      : 'Preparing leads for validation...'
                    }
                  </span>
                  {validationProgress?.chainsFound > 0 && (
                    <span className="text-orange-600">{validationProgress.chainsFound} chains detected</span>
                  )}
                </div>
              </div>
            )}

            {/* Completed summary stats */}
            {isCompleted && completedStats && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Total Leads" value={completedStats.totalLeads || completedStats.leadsFound || 0} color="green" />
                <StatCard label="New" value={completedStats.newLeads || 0} color="blue" />
                <StatCard label="Chains" value={completedStats.chains || completedStats.chainsFound || 0} color="orange" />
                <StatCard label="Validated" value={completedStats.validated || completedStats.totalValidated || 0} color="purple" />
              </div>
            )}
          </div>
        );
      })()}

      {/* Scrape History */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Extraction History</h3>
          <button onClick={loadData} className="text-gray-400 hover:text-gray-600">
            <RefreshCw size={16} />
          </button>
        </div>

        {scrapeJobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No extractions yet. Start one above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">ID</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Member</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Category</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Leads</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">New</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Chains</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">API Calls</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Started</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scrapeJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500">#{job.id}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-4 py-2 text-gray-700">{job.member_name || 'All'}</td>
                    <td className="px-4 py-2 text-gray-700">{job.category_name || 'All'}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{job.leads_found || 0}</td>
                    <td className="px-4 py-2 text-right text-green-600">{job.new_leads || 0}</td>
                    <td className="px-4 py-2 text-right text-orange-600">{job.chains_found || 0}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{job.total_api_calls || 0}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">
                      {job.started_at ? new Date(job.started_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

const StatCard = ({ label, value, color = 'gray' }) => {
  const colorClasses = {
    gray: 'bg-gray-50 text-gray-800',
    green: 'bg-green-50 text-green-800',
    blue: 'bg-blue-50 text-blue-800',
    orange: 'bg-orange-50 text-orange-800',
    purple: 'bg-purple-50 text-purple-800'
  };

  return (
    <div className={`rounded-lg px-3 py-2 ${colorClasses[color]}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-gray-100 text-gray-600',
    running: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-orange-100 text-orange-700'
  };

  const icons = {
    pending: <Clock size={12} />,
    running: <Loader2 size={12} className="animate-spin" />,
    completed: <CheckCircle size={12} />,
    failed: <XCircle size={12} />,
    cancelled: <AlertCircle size={12} />
  };

  return (
    <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {icons[status]}
      <span>{status}</span>
    </span>
  );
};

export default ScrapeTab;
